// MUST stay the first import: it leaves legacy production hosts before
// anything below evaluates. See the file for why it is not a Hosting redirect.
import './canonicalHostRedirect';
import './zodJitless';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import './fonts';
import 'react-material-symbols/rounded';
import App from './App';
import theme from './theme/index';
import { VendorDarkModeBridge } from './theme/VendorDarkModeBridge';
import { ColorModePreferenceSync } from './theme/ColorModePreferenceSync';
import { PrintLightMode } from './theme/PrintLightMode';
import { BrandIconGradientDefs } from './components/ui/BrandIconGradientDefs';
import { safeAnalytics } from './utils/analytics';
import { isBrowserExtensionError } from './utils/errorFilters';
import { attemptStaleChunkReload } from './utils/lazyWithRetry';
import { ANALYTICS_EVENTS } from './constants/analytics';
import { stashEmailCtaFromUrl } from './utils/emailCtaCapture';
import { flushPendingAnalyticsEvents } from './utils/analyticsConsentGrant';
import { initBowerFirstTouch } from './utils/bowerFirstTouch';
import { onSessionRestored } from './services/sessionRestored';
import { startBootItemsPrefetch } from './lib/bootItemsPrefetch';
import { preloadBootRoute } from './lib/bootRoutePreload';
import { trackBackNavigation } from './utils/backNavigation';
import { listenForCspViolations } from './utils/cspViolationTelemetry';

// Every CSP violation becomes an analytics event, stashed until init (#4771).
listenForCspViolations();

// Capture utm_source=email params synchronously, BEFORE React mounts and the
// auth/onboarding redirect chain strips window.location.search. The stashed
// payload is flushed as an `Email CTA Clicked` event once analytics is active.
stashEmailCtaFromUrl();

// Snapshot first-touch attribution + mint a visitor id BEFORE React
// mounts and BEFORE PostHog initialises. Independent of cookie consent
// and ad blockers — uses only our own localStorage. Read later by
// `useSignupSignals` so signup attribution survives no-consent and
// PostHog-blocked visitors.
initBowerFirstTouch();

// Data router — required for `useBlocker` (navigation guards) and
// `useFetcher` / `useSubmit` to work. The whole app sits under a single
// catch-all route whose element is <App />, which keeps the existing
// provider + <Routes> tree intact. We do not need to migrate every route
// definition; only the router shell changes.
//
// Tradeoff: RR loaders/actions and `useRouteLoaderData` do NOT work on the
// descendant routes declared inside <App />. Those APIs only run on routes
// registered in this config. If we ever want loader-based data fetching,
// promote the affected routes out of <App />'s <Routes> and into this
// `createRoutesFromElements` tree.
const router = createBrowserRouter(createRoutesFromElements(<Route path="*" element={<App />} />));

// Every back arrow returns to the page the reader came from
// (`utils/backNavigation`). Before <RouterProvider>, so each navigation is
// recorded before the page it lands on renders.
trackBackNavigation(router);

/**
 * Main entry point for the Bower frontend application
 * Sets up providers and renders the App component
 *
 * State Management:
 * - TanStack Query: Server state (data fetching, caching, sync)
 * - React Context: Auth, Workspace, UI state
 * - localStorage: Persistence for workspace ID, sidebar state, theme
 */

// Initialize Analytics — deferred off the critical path. PostHog.init() does
// feature-flag fetches, autocapture observer hookup, and session-recording
// bundle work that can take 100–200 ms on mobile. Running it during idle time
// after first paint keeps it off the LCP path without dropping events —
// safeAnalytics.track() pre-init returns early (isActive=false guard), and
// PostHog's autocapture catches the initial $pageview once it loads.
// Events fired between app boot and idle callback are intentionally skipped.
const initAnalytics = () => {
  safeAnalytics.init();
  // `ui_version` told the legacy workspace UI from the folders UI. One UI is
  // left, so overwrite the value PostHog persisted per browser: here, after
  // init, because registerContext is a no-op while analytics is down.
  safeAnalytics.registerContext({ ui_version: null });
  // Boot-time events (the auth-persistence recovery, the recording-recovery
  // prompt, a marketing-email landing) fire before this, so their own track()
  // would be dropped at the !isActive gate the comment above describes. They
  // stash instead, and this is the delivery. Without consent each flush keeps
  // its stash; the consent grant and the auth-state listener re-flush.
  flushPendingAnalyticsEvents();
};
if (typeof requestIdleCallback === 'function') {
  // requestIdleCallback runs in browser idle periods; 2s timeout is a
  // safety net for busy tabs where idle never arrives.
  requestIdleCallback(initAnalytics, { timeout: 2000 });
} else {
  // Safari < 17 + a few old Android browsers lack requestIdleCallback.
  setTimeout(initAnalytics, 100);
}

// Safety net: reload on stale-chunk errors that slip past lazyWithRetry
// (e.g. dynamic imports triggered outside React.lazy)
globalThis.addEventListener('unhandledrejection', (event) => {
  // Filter browser extension noise (password managers, autofill)
  if (isBrowserExtensionError(event.reason)) {
    event.preventDefault();
    safeAnalytics.track(ANALYTICS_EVENTS.BROWSER_EXTENSION_ERROR_SUPPRESSED, {
      // The name, never the message: it can carry page text.
      error_name: event.reason instanceof Error ? event.reason.name : typeof event.reason,
    });
    return;
  }

  // Reload on stale-chunk errors — same per-chunk one-shot guard (and
  // session cap) as lazyWithRetry, so both paths share one policy.
  attemptStaleChunkReload(event.reason);
});

// Before the first render, so it is in place when Firebase restores the
// session: the open page's code and the open folder's page 1 then load
// alongside /auth/login.
onSessionRestored((uid, idToken) => {
  preloadBootRoute();
  startBootItemsPrefetch(uid, idToken);
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ColorModeScript initialColorMode={theme.config.initialColorMode} />
    <ChakraProvider
      theme={theme}
      toastOptions={{
        defaultOptions: {
          isClosable: true,
          variant: 'left-accent',
        },
      }}
    >
      <VendorDarkModeBridge />
      <ColorModePreferenceSync />
      <PrintLightMode />
      {/* Owns the <linearGradient> defs every gradient-filled BowerIcon
          references by id. Must be inside ChakraProvider — its stops are
          `--chakra-colors-*` vars — and rendered exactly once. */}
      <BrandIconGradientDefs />
      <RouterProvider router={router} />
    </ChakraProvider>
  </React.StrictMode>,
);
